import React, { useEffect, useState } from 'react';
import {
    listBlacklists,
    createBlacklist,
    deleteBlacklist,
} from '../../api/lfApiBlacklist';

const initialState = {
    entries: [],
    loading: false,
    submitting: false,
    error: '',
};

const Blacklist = ({ childProfile }) => {
    const childProfileId = childProfile?.id;

    const [entries, setEntries] = useState(initialState.entries);
    const [loading, setLoading] = useState(initialState.loading);
    const [submitting, setSubmitting] = useState(initialState.submitting);
    const [error, setError] = useState(initialState.error);
    const [newText, setNewText] = useState('');

    useEffect(() => {
        if (!childProfileId) {
            setEntries([]);
            return;
        }

        let isMounted = true;
        setLoading(true);
        setError('');

        listBlacklists(childProfileId, (message) => {
            if (isMounted) {
                setError(message);
            }
        })
            .then((result) => {
                if (isMounted && Array.isArray(result)) {
                    setEntries(result);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [childProfileId]);

    if (!childProfileId) {
        return null;
    }

    const handleAdd = async () => {
        const text = newText.trim();
        if (!text) {
            setError('Text cannot be empty.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const entry = await createBlacklist(childProfileId, text, setError);
            if (entry) {
                setEntries((prev) => [...prev, entry]);
                setNewText('');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (blacklistId) => {
        setSubmitting(true);
        setError('');

        try {
            const deleted = await deleteBlacklist(blacklistId, setError);
            if (deleted) {
                setEntries((prev) => prev.filter((entry) => entry.id !== blacklistId));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mt-10">
            <h2 className="text-xl font-semibold">Blocked keywords</h2>
            {error && <div className="mt-2 text-sm text-red-500">{error}</div>}
            {loading ? (
                <div className="mt-4 text-sm text-gray-500">Loading...</div>
            ) : (
                <>
                    <div className="mt-4 px-3 py-2 rounded-lg bg-pastelgreen-light text-sm text-darkrock leading-snug">
                        ✓ Videos matching these keywords won&apos;t be recommended or shown in search
                    </div>
                    {entries.length === 0 ? (
                        <div className="mt-4 text-sm text-gray-500">No blocked keywords yet.</div>
                    ) : (
                        <div className="mt-4 flex flex-wrap gap-2">
                            {entries.map((entry) => (
                                <span
                                    key={entry.id}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pastelyellow text-darkrock text-sm font-semibold"
                                >
                                    {entry.text}
                                    <button
                                        className="text-darkrock/40 hover:text-darkrock/70 leading-none"
                                        onClick={() => handleDelete(entry.id)}
                                        disabled={submitting}
                                        aria-label={`Remove ${entry.text}`}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                        <input
                            type="text"
                            value={newText}
                            onChange={(e) => setNewText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                            placeholder="Add keyword..."
                            className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                            disabled={submitting}
                        />
                        <button
                            className="bg-blue-500 text-white px-4 py-2 rounded text-sm disabled:bg-blue-300"
                            onClick={handleAdd}
                            disabled={submitting || newText.trim().length === 0}
                        >
                            Add
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Blacklist;
